import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { issuedLetterService, employeeService } from "../../../services/api";
import { DsButton } from "../../../design-system";
import { ISSUED_LETTER_TYPES } from "./IssuedByCompanyPanel.jsx";

/**
 * Admin bulk upload for company migration:
 * - one_to_many: one PDF → many employees
 * - filename_map: many PDFs mapped by emp code in filename
 */
export default function IssuedLettersBulkUploadModal({ open, onClose, onDone }) {
  const [mode, setMode] = useState("filename_map");
  const [letterType, setLetterType] = useState("appointment");
  const [files, setFiles] = useState([]);
  const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;
  const [employees, setEmployees] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [empFilter, setEmpFilter] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await employeeService.getEmployees({ limit: 5000 });
        const list =
          res?.employees ||
          res?.data?.employees ||
          res?.data ||
          (Array.isArray(res) ? res : []);
        if (!cancelled) setEmployees(Array.isArray(list) ? list : []);
      } catch (e) {
        console.warn("Load employees for bulk issued letters:", e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  const filteredEmployees = useMemo(() => {
    const q = empFilter.trim().toLowerCase();
    if (!q) return employees.slice(0, 200);
    return employees
      .filter((e) => {
        const name = [e.user?.name, e.user?.lastName, e.name].filter(Boolean).join(" ").toLowerCase();
        const code = String(e.empCode || "").toLowerCase();
        return name.includes(q) || code.includes(q);
      })
      .slice(0, 200);
  }, [employees, empFilter]);

  if (!open) return null;

  const toggleEmp = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleSubmit = async () => {
    if (!files.length) {
      toast.error("Select at least one PDF");
      return;
    }
    for (const f of files) {
      if (f.size > MAX_UPLOAD_BYTES) {
        toast.error(`${f.name}: File exceeds 10MB limit`);
        return;
      }
    }
    if (mode === "one_to_many" && !selectedIds.length) {
      toast.error("Select at least one employee");
      return;
    }
    setBusy(true);
    try {
      const form = new FormData();
      form.append("mode", mode);
      form.append("letterType", letterType);
      if (mode === "one_to_many") {
        form.append("employeeIds", JSON.stringify(selectedIds));
        form.append("file", files[0]);
      } else {
        files.forEach((f) => form.append("files", f));
      }
      const res = await issuedLetterService.bulkUpload(form);
      const count = res?.data?.acceptedCount ?? 0;
      if (count > 0) toast.success(`${count} letter(s) uploaded`);
      (res?.data?.rejected || []).forEach((r) => {
        toast.error(`${r.fileName || r.employeeId}: ${r.reason}`);
      });
      onDone?.(res);
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || "Bulk upload failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[140] flex items-center justify-center bg-black/45 p-4">
      <div className="max-h-[92vh] w-full max-w-2xl overflow-y-auto rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] p-5 shadow-xl">
        <h3 className="text-base font-semibold">Upload issued letters (bulk)</h3>
        <p className="mt-1 text-xs text-[hsl(var(--ds-muted-fg))]">
          Use filename map for many files named Name(EmpCode).pdf or EmpCode_….pdf, or one file for
          many employees. Max 10MB per PDF; status will be Manually uploaded (lossless compress on
          store).
        </p>

        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <label className="text-sm">
            <span className="mb-1 block text-[hsl(var(--ds-muted-fg))]">Mode</span>
            <select
              value={mode}
              onChange={(e) => setMode(e.target.value)}
              className="w-full rounded-md border border-[hsl(var(--ds-border))] bg-transparent px-2 py-1.5"
            >
              <option value="filename_map">Many files → many employees (filename)</option>
              <option value="one_to_many">One file → many employees</option>
            </select>
          </label>
          <label className="text-sm">
            <span className="mb-1 block text-[hsl(var(--ds-muted-fg))]">Letter type</span>
            <select
              value={letterType}
              onChange={(e) => setLetterType(e.target.value)}
              className="w-full rounded-md border border-[hsl(var(--ds-border))] bg-transparent px-2 py-1.5"
            >
              {ISSUED_LETTER_TYPES.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </label>
          <label className="text-sm sm:col-span-2">
            <span className="mb-1 block text-[hsl(var(--ds-muted-fg))]">PDF file(s)</span>
            <input
              type="file"
              accept="application/pdf,.pdf"
              multiple={mode === "filename_map"}
              onChange={(e) => setFiles(Array.from(e.target.files || []))}
            />
          </label>
        </div>

        {mode === "one_to_many" && (
          <div className="mt-4">
            <input
              type="text"
              value={empFilter}
              onChange={(e) => setEmpFilter(e.target.value)}
              placeholder="Search employees…"
              className="mb-2 w-full rounded-md border border-[hsl(var(--ds-border))] bg-transparent px-2 py-1.5 text-sm"
            />
            <div className="max-h-48 overflow-y-auto rounded-md border border-[hsl(var(--ds-border))]">
              {filteredEmployees.map((emp) => {
                const id = Number(emp.id);
                const label = [
                  emp.user?.name || emp.name,
                  emp.user?.lastName,
                  emp.empCode ? `(${emp.empCode})` : "",
                ]
                  .filter(Boolean)
                  .join(" ");
                return (
                  <label
                    key={id}
                    className="flex cursor-pointer items-center gap-2 border-b border-[hsl(var(--ds-border))] px-3 py-1.5 text-sm last:border-b-0"
                  >
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(id)}
                      onChange={() => toggleEmp(id)}
                    />
                    <span>{label}</span>
                  </label>
                );
              })}
            </div>
            <p className="mt-1 text-xs text-[hsl(var(--ds-muted-fg))]">
              {selectedIds.length} employee(s) selected
            </p>
          </div>
        )}

        <div className="mt-5 flex justify-end gap-2">
          <DsButton type="button" variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </DsButton>
          <DsButton type="button" onClick={handleSubmit} disabled={busy}>
            {busy ? "Uploading…" : "Upload"}
          </DsButton>
        </div>
      </div>
    </div>
  );
}
